import {TrustAccountGuestRole} from '@sollapay/enums';
import {useTranslation} from 'react-i18next';

import {showResultToastCard} from '@/components';

import {useTrustAccountGuestMutations} from './useTrustAccountGuestMutations';
import {getGuestDisplayName} from '../utils';

import type {TrustAccountGuestDTO} from '@sollapay/types';

/**
 * The share panel's guest actions, each confirmed with a toast. A refused invite is rethrown so the
 * panel can mark the refused chips.
 */
export function useShareGuestActions(trustAccountId: string, guests: TrustAccountGuestDTO[]) {
  const {t} = useTranslation('trustAccounts');
  const mutations = useTrustAccountGuestMutations();

  const toast = (isError: boolean, description: string) =>
    showResultToastCard({
      isError,
      description,
      title: t(isError ? 'share.toast.errorTitle' : 'share.toast.title'),
      closeLabel: t('share.toast.close')
    });

  const run = async (
    action: () => Promise<unknown>,
    success: string,
    {rethrow = false}: {rethrow?: boolean} = {}
  ) => {
    try {
      await action();
      toast(false, success);
    } catch (error) {
      if (rethrow) throw error;
      toast(true, t('share.toast.failed'));
    }
  };

  const findGuest = (guestId: string) => guests.find(({userId}) => userId === guestId);
  const guestLabel = (guest: TrustAccountGuestDTO | undefined) =>
    guest && getGuestDisplayName(guest);

  const invite = (emails: string[]) =>
    run(
      () =>
        mutations.invite.mutateAsync({trustAccountId, emails, role: TrustAccountGuestRole.VIEWER}),
      t('share.toast.invited', {count: emails.length, email: emails[0]}),
      {rethrow: true}
    );

  const remove = (guestId: string) => {
    const guest = findGuest(guestId);
    const message = guest?.status === 'pending' ? 'share.toast.cancelled' : 'share.toast.removed';
    return run(
      () => mutations.remove.mutateAsync({trustAccountId, guestId}),
      t(message, {guest: guestLabel(guest)})
    );
  };

  const resend = (guestId: string) =>
    run(
      () => mutations.resend.mutateAsync({trustAccountId, guestId}),
      t('share.toast.resent', {guest: guestLabel(findGuest(guestId))})
    );

  return {invite, remove, resend};
}
